import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { browserOffline, cacheRows, cachedRows, type Message } from '@dia/db';
import { useAuth } from '../../auth';
import { supabase } from '../../supabase';
import { unreadCount } from '../logic/helpSheet';
import { useAccess } from './useAccess';

/**
 * How many host replies the guest has not seen, for the gold dot on the help
 * button and the count on the Messages row.
 *
 * "Seen" is kept on this phone: opening the help thread (in the sheet, or on
 * /mensajes or /pedidos, which now show the same thread) marks everything up
 * to that moment as read. There are no read receipts on the server (Section 10), and
 * this adds none; it is only the phone remembering when it last looked.
 *
 * The thread is the same read Mensajes makes, written to the same cache, so
 * the dot works offline with what the phone last saw. It asks the island at
 * most once a minute, and not at all while the tab is hidden.
 */

const CHECK_EVERY_MS = 120_000;
const MIN_GAP_MS = 60_000;

function seenKey(stayId: string): string {
  return `dia.guest.messages.seen.${stayId}`;
}

function readSeen(stayId: string): string | null {
  try {
    return globalThis.localStorage?.getItem(seenKey(stayId)) ?? null;
  } catch {
    return null;
  }
}

function writeSeen(stayId: string): void {
  try {
    globalThis.localStorage?.setItem(seenKey(stayId), new Date().toISOString());
  } catch {
    // Private mode. The dot may come back once, which is harmless.
  }
}

/** Fired when the thread has been looked at, so the dot goes out at once. */
const SEEN_EVENT = 'dia:help-thread-seen';

/**
 * The help thread calls this when it opens and when it closes. It works the
 * same wherever the thread is drawn, inside the sheet or on its own page.
 */
export function markThreadSeen(stayId: string): void {
  writeSeen(stayId);
  try {
    window.dispatchEvent(new Event(SEEN_EVENT));
  } catch {
    // No window (a test). Nothing to tell.
  }
}

/** The two addresses that now draw the whole thread. */
const THREAD_PATHS = new Set(['/mensajes', '/pedidos']);

export function useUnreadReplies(): number {
  const { session } = useAuth();
  const access = useAccess();
  const { pathname } = useLocation();
  const [count, setCount] = useState(0);
  const lastFetch = useRef(0);

  const stayId = session?.stay.id;
  const open = Boolean(session && access?.personalOpen);
  const onThread = THREAD_PATHS.has(pathname);

  // The thread looked at from anywhere: the dot goes out now, not at the next poll.
  useEffect(() => {
    const onSeen = () => setCount(0);
    window.addEventListener(SEEN_EVENT, onSeen);
    return () => window.removeEventListener(SEEN_EVENT, onSeen);
  }, []);

  // Being on the thread's page is reading it. Mark on the way in and on the way out, so
  // a reply that arrived while it was open does not light the dot afterwards.
  useEffect(() => {
    if (!stayId || !onThread) return;
    writeSeen(stayId);
    setCount(0);
    return () => writeSeen(stayId);
  }, [stayId, onThread]);

  useEffect(() => {
    if (!stayId || !open) {
      setCount(0);
      return;
    }
    let live = true;

    const check = async (force: boolean) => {
      if (document.visibilityState === 'hidden') return;
      let rows: Message[] | null = null;
      const due = force || Date.now() - lastFetch.current > MIN_GAP_MS;
      if (due && !browserOffline()) {
        try {
          const { data, error } = await supabase
            .from('messages')
            .select('*')
            .eq('thread_kind', 'stay')
            .eq('stay_id', stayId)
            .order('created_at', { ascending: true });
          if (error) throw new Error(error.message);
          rows = await cacheRows('messages:stay', (data ?? []) as Message[]);
          lastFetch.current = Date.now();
        } catch {
          rows = null;
        }
      }
      if (!rows) rows = (await cachedRows<Message>('messages:stay')).filter((m) => m.stay_id === stayId);
      if (live) setCount(onThread ? 0 : unreadCount(rows, readSeen(stayId)));
    };

    void check(false);
    const poll = setInterval(() => void check(true), CHECK_EVERY_MS);
    const onVisible = () => void check(false);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      live = false;
      clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [stayId, open, onThread]);

  return count;
}
